import { gql } from '@apollo/client';

export const MY_REPUTATION_BALANCE_QUERY = gql`
  query MyReputationBalance {
    myReputationBalance {
      balance
      hasEverEarned
      spendablePoints
    }
  }
`;

export interface ReputationBalance {
  balance: number;
  hasEverEarned: boolean;
  /**
   * What the member can spend. Diverges from `balance` at their first redemption — spending
   * never reduces reputation, which is what the leaderboard ranks on.
   */
  spendablePoints: number;
}

/*
 * The reward list on a points milestone notification. `viewerOutcome` is left out on purpose:
 * it reads Stripe, and the panel only needs what the database already knows.
 */
export const MILESTONE_REWARD_CATALOG_QUERY = gql`
  query MilestoneRewardCatalog {
    redemptionRewards {
      id
      slug
      title
      pointCost
      isRedeemable
      viewerBlockedReason
    }
  }
`;

export interface MilestoneCatalogReward {
  id: string;
  slug: string;
  title: string;
  pointCost: number;
  isRedeemable: boolean;
  viewerBlockedReason?: string | null;
}

/*
 * Ported verbatim from town-square (see docs/decisions/0002-shared-react-components.md: React
 * components are duplicated into both apps rather than shared, because `shared/` is framework-free).
 * The reputation popover must say the same thing in both apps — a member who sees six earn rules
 * on arthelper.com and three in the app has been told two different things about how points work.
 */

export const MY_REPUTATION_SOURCES_QUERY = gql`
  query MyReputationSources($first: Int = 10, $after: String) {
    myReputationSources(first: $first, after: $after) {
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
      edges {
        cursor
        node {
          id
          bucket
          netPoints
          eventCount
          lastEarnedAt
          post {
            id
            title
            slug
            communitySlug
            isDeleted
          }
          commentSnippet
          bountyName
          referralUser {
            asfId
            firstName
            lastName
            username
            profile {
              slug
              displayName
            }
          }
        }
      }
    }
  }
`;

/** The member behind a referral bucket — the only actor a source row ever names. */
export interface ReputationLedgerActor {
  asfId: string;
  firstName?: string | null;
  lastName?: string | null;
  username?: string | null;
  profile?: {
    slug?: string | null;
    displayName?: string | null;
    avatarUrl?: string | null;
  } | null;
}

export interface ReputationSourcePost {
  id: string;
  title: string;
  slug: string;
  communitySlug: string;
  isDeleted: boolean;
}

export interface ReputationSource {
  id: string;
  bucket: 'post' | 'comment' | 'referral' | 'quest' | 'first-touch' | 'bounty' | 'initial';
  netPoints: number;
  eventCount: number;
  lastEarnedAt: string;
  post?: ReputationSourcePost | null;
  commentSnippet?: string | null;
  referralUser?: ReputationLedgerActor | null;
  /** The bounty a bounty bucket is for. */
  bountyName?: string | null;
}

export interface ReputationSourceConnection {
  pageInfo: {
    hasNextPage: boolean;
    endCursor?: string | null;
  };
  totalCount: number;
  edges: Array<{
    cursor: string;
    node: ReputationSource;
  }>;
}
